/**
 * Zustand Help Store (AC-651)
 *
 * Manages help system state including:
 * - Help panel visibility (open/close/toggle)
 * - Document navigation and content
 * - Search functionality (query, results, loading states)
 * - Navigation to specific help topics
 *
 * Technical Design Reference: AC-648 (Epic: Contextual Help System)
 * Related Tickets:
 * - AC-648 (Epic: Contextual Help System)
 * - AC-651 (Frontend - helpStore Zustand store)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  DocCategory,
  DocSearchResult,
  HelpState,
  HelpActions,
  HelpStore,
} from '../types/help';

// Initial state
const initialState: HelpState = {
  isHelpOpen: false,
  currentDocPath: null,
  searchQuery: '',
  searchResults: [],
  isSearching: false,
  docContent: null,
  isLoadingContent: false,
  docNavigation: [],
};

/**
 * Help store for managing the contextual help system.
 */
export const useHelpStore = create<HelpStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      ...initialState,

      // =======================================================================
      // VISIBILITY ACTIONS
      // =======================================================================

      /**
       * Open the help panel.
       */
      openHelp: () => {
        set({ isHelpOpen: true });
      },

      /**
       * Close the help panel.
       * Clears search state but preserves navigation and current doc path.
       */
      closeHelp: () => {
        set({
          isHelpOpen: false,
          searchQuery: '',
          searchResults: [],
        });
      },

      /**
       * Toggle the help panel open/close state.
       */
      toggleHelp: () => {
        set((state) => ({ isHelpOpen: !state.isHelpOpen }));
      },

      /**
       * Open help panel and navigate to a specific topic.
       * Clears any active search state.
       */
      openHelpToTopic: (docPath: string) => {
        set({
          isHelpOpen: true,
          currentDocPath: docPath,
          searchQuery: '',
          searchResults: [],
        });
      },

      // =======================================================================
      // SEARCH ACTIONS
      // =======================================================================

      /**
       * Set the search query.
       */
      setSearchQuery: (query: string) => {
        set({ searchQuery: query });
      },

      /**
       * Set search results.
       */
      setSearchResults: (results: DocSearchResult[]) => {
        set({ searchResults: results });
      },

      /**
       * Clear all search state (query, results, and isSearching).
       */
      clearSearch: () => {
        set({
          searchQuery: '',
          searchResults: [],
          isSearching: false,
        });
      },

      // =======================================================================
      // CONTENT ACTIONS
      // =======================================================================

      /**
       * Set the loaded document content.
       */
      setDocContent: (content: string | null) => {
        set({ docContent: content });
      },

      /**
       * Set the documentation navigation structure.
       */
      setDocNavigation: (nav: DocCategory[]) => {
        set({ docNavigation: nav });
      },

      // =======================================================================
      // LOADING STATE ACTIONS
      // =======================================================================

      /**
       * Set the isSearching loading state.
       */
      setIsSearching: (value: boolean) => {
        set({ isSearching: value });
      },

      /**
       * Set the isLoadingContent loading state.
       */
      setIsLoadingContent: (value: boolean) => {
        set({ isLoadingContent: value });
      },
    }),
    { name: 'help-store' }
  )
);

// Export selectors for optimized component subscriptions
export const selectIsHelpOpen = (state: HelpStore) => state.isHelpOpen;
export const selectCurrentDocPath = (state: HelpStore) => state.currentDocPath;
export const selectSearchQuery = (state: HelpStore) => state.searchQuery;
export const selectSearchResults = (state: HelpStore) => state.searchResults;
export const selectIsSearching = (state: HelpStore) => state.isSearching;
export const selectDocContent = (state: HelpStore) => state.docContent;
export const selectIsLoadingContent = (state: HelpStore) => state.isLoadingContent;
export const selectDocNavigation = (state: HelpStore) => state.docNavigation;

export default useHelpStore;
